/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthenticatedHttpClient } from "@/lib/axios";
import { AuthProvider } from "@/types/common";
import {
  EmailHealthHistoryResponse,
  EmailHealthResult,
} from "@/types/email-health";

export class HealthTestService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async createNewAlias(alias: string) {
    const response = await this.http.post("/email-health-checker/alias", {
      alias,
    });
    return response as unknown as { alias: string };
  }

  generateRandomAlias() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `health-test-${random}-${timestamp}`;
  }

  async generateBulkAliases(numberOfAliases: number) {
    const response = await this.http.post("/email-health-checker/alias/bulk", {
      aliases: numberOfAliases,
    });
    return response as unknown as string[];
  }

  async getEmailHealthResults(alias: string) {
    const response = await this.http.get(
      `/email-health-checker/results/${alias}`
    );
    return response as unknown as EmailHealthResult;
  }

  async getCompanyEmailResults({
    page,
    limit,
    from,
    to,
    domain,
  }: {
    page: number;
    limit: number;
    from: string;
    to: string;
    domain: string;
  }) {
    const response = await this.http.get(
      `/email-health-checker/company-results?page=${page}&limit=${limit}&from=${from}&to=${to}&domain=${domain}`
    );
    return response as unknown as EmailHealthHistoryResponse;
  }

  async exportEmailResults(from: Date, to: Date, domain: string) {
    return await this.http.get(
      `/email-health-checker/export?from=${from}&to=${to}&domain=${domain}`,
      { responseType: "blob" }
    );
  }

  async runBulkEmailTest(domains: string[]) {
    // const results = await Promise.all(
    //   domains.map(async (domain) => {
    //     const randomAlias = this.generateRandomAlias();
    //     const newAlias = await this.createNewAlias(randomAlias);
    //     return { domain, testEmail: newAlias.alias };
    //   })
    // );

    const results = await this.generateBulkAliases(domains.length);
    return domains.map((domain, index) => ({
      domain,
      testEmail: results[index],
    }));
  }

  async createScheduledTest(
    domains: string[],
    type: "placement" | "email-health",
    espType: AuthProvider,
    schedule: {
      type: "once" | "recurring";
      recurrenceType: string;
      startTime: Date;
      customRecurrence?: {
        interval: number;
        unit: string;
        endType: string;
        endDate?: Date;
        occurrences?: number;
        weekdays?: number[];
      };
    }
  ) {
    const response = await this.http.post(
      "/email-health-checker/scheduled-tests",
      {
        domains,
        schedule,
        type,
        espType,
      }
    );
    return response as unknown as any;
  }

  async getScheduledTests(type: "placement" | "email-health") {
    const response = await this.http.get(
      `/email-health-checker/scheduled-tests?type=${type}`
    );
    return response;
  }

  async getPlacementEmailTests() {
    const response = await this.http.get(
      "/email-health-checker/placement-tests"
    );
    return response;
  }

  async deleteScheduledTest(id: string) {
    const response = await this.http.delete(
      `/email-health-checker/scheduled-tests/${id}`
    );
    return response;
  }

  async updateScheduledTest(
    id: string,
    domains: string[],
    schedule: {
      type: "once" | "recurring";
      recurrenceType: string;
      startTime: Date;
      customRecurrence?: {
        interval: number;
        unit: string;
        endType: string;
        endDate?: Date;
        occurrences?: number;
        weekdays?: number[];
      };
    }
  ) {
    const response = await this.http.patch(
      `/email-health-checker/scheduled-tests/${id}`,
      {
        domains,
        schedule,
      }
    );
    return response;
  }

  async getDeliveryStatus(domain: string) {
    const response = await this.http.get(
      `/email-health-checker/delivery-status-check?domain=${domain}`
    );
    return response as unknown as {
      processing: boolean;
      stage: string;
      domain: string;
      companyId: string;
      startedAt: string;
      elapsedTime: number;
    };
  }

  createDeliveryStatusStream(domain: string): EventSource {
    // Use absolute URL to ensure it works in all environments
    const baseUrl = process.env.NEXT_PUBLIC_API_URL;
    const url = `${baseUrl}/email-health-checker/delivery-status?domain=${domain}`;
    return new EventSource(url);
  }
}
